import Input from '@/shared/components/rebranded/Input/Input';
import { getText } from '@storebrand-digital/fms-translations';
import { useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FieldSet, Label } from '../../shared/components/Form/Form';
import { FieldProps, StringSchema } from '../types';
import { joinErrors } from '../validation/utils';
import { hasErrors } from '../validation/validateSchema';
import {
  DATE_FORMATS,
  convertToDate,
  formatDateTime
} from './DateField/DateField';
import styles from './DateField/DateField.module.css';
import { HourPicker } from './HourPicker';
import './react-datepicker.overrides.css';

type DateAndTimeValue = string | null;
type DateAndTimeFieldProps = FieldProps<StringSchema, DateAndTimeValue>;

const maxDate = (schema: StringSchema) =>
  schema.format === 'date-time-not-future' ||
  schema.format === 'date-not-future'
    ? new Date()
    : undefined;

export const DateAndTimeField = ({
  id,
  schema,
  name,
  value,
  onChange,
  onBlur,
  touched,
  forceShowError,
  errors,
  readOnly
}: DateAndTimeFieldProps) => {
  const handleChange = useCallback(
    (date: Date | null) => {
      onChange({
        target: {
          name,
          value: date == null ? '' : formatDateTime(date)
        }
      });
    },
    [name, onChange]
  );

  const handleBlur = () => {
    onBlur({
      target: {
        name,
        touched: true
      }
    });
  };

  const showError = !!((touched || forceShowError) && hasErrors(errors));
  const error = showError ? joinErrors(errors, getText) : undefined;

  const dateValue = convertToDate(value);

  return (
    <FieldSet>
      <div>
        <Label htmlFor={id} error={showError}>
          {schema.title}
        </Label>
      </div>
      {schema.$uiOptions?.hint && (
        <div style={{ marginBottom: 16, marginTop: 8 }}>
          <span className="dsm-form-hint">{schema.$uiOptions.hint}</span>
        </div>
      )}

      <div>
        <DatePicker
          id={id}
          locale="nb"
          autoComplete="off"
          dateFormat={DATE_FORMATS}
          placeholderText="DD.MM.YYYY"
          calendarClassName={styles.calendar}
          maxDate={maxDate(schema)}
          selected={dateValue}
          onChange={handleChange}
          onBlur={handleBlur}
          customInput={
            <Input
              id={`${id}_startDate_input`}
              name={`${id}_startDate_input`}
              inputProps={{
                className: styles.innerInput
              }}
              medium
              error={error}
              className={styles.input}
            />
          }
          disabled={readOnly}
        />

        <div style={{ marginTop: 16, marginBottom: 8 }}>
          <Label htmlFor={`${id}_time`} error={!!error}>
            Tidspunkt
          </Label>
        </div>
        <HourPicker
          id={`${id}_time`}
          name={name}
          onChange={handleChange}
          value={dateValue}
          error={error}
          disabled={readOnly}
        />
      </div>
    </FieldSet>
  );
};
